import { createContext, useContext, useEffect, useState, useCallback } from "react";

const MAX_COMPARE = 3;
const STORAGE_KEY = "cm_compare_v1";

const CompareCtx = createContext(null);

export function CompareProvider({ children }) {
  // Synchronously hydrate from localStorage so the persist effect's first run
  // already sees the stored value (avoids race that wipes storage on reload).
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // Persist whenever items change
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* noop */ }
  }, [items]);

  const isInCompare = useCallback((id) => items.some((x) => x.id === id), [items]);

  const toggle = useCallback((listing) => {
    setItems((prev) => {
      if (prev.some((x) => x.id === listing.id)) {
        return prev.filter((x) => x.id !== listing.id);
      }
      if (prev.length >= MAX_COMPARE) {
        return prev; // caller should toast
      }
      // Store only the fields we need for the tray + compare page
      const slim = {
        id: listing.id,
        brand: listing.brand,
        model: listing.model,
        year: listing.year,
        variant: listing.variant,
        price: listing.price,
        daily_price: listing.daily_price,
        listing_type: listing.listing_type,
        mileage: listing.mileage,
        transmission: listing.transmission,
        fuel_type: listing.fuel_type,
        body_type: listing.body_type,
        engine_size: listing.engine_size,
        colour: listing.colour,
        condition: listing.condition,
        location: listing.location,
        images: listing.images?.slice(0, 1) || [],
      };
      return [...prev, slim];
    });
  }, []);

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  return (
    <CompareCtx.Provider value={{ items, toggle, remove, clear, isInCompare, max: MAX_COMPARE }}>
      {children}
    </CompareCtx.Provider>
  );
}

export function useCompare() {
  const ctx = useContext(CompareCtx);
  if (!ctx) throw new Error("useCompare must be used inside CompareProvider");
  return ctx;
}
